<template>
  <div class="row">
    <div class="col-md-2 order-sm-12">
      <card>
        <div slot="header">
          <h5 class="card-title">Contacts</h5>
        </div>
        <div>
          <span class="text-muted" v-if="!loading"
            >Showing {{ contacts.length }} of {{ totalContacts }} contacts</span
          >
          <span class="text-muted" v-if="loading">
            <i class="fa fa-circle-o-notch spin"></i>&nbsp; Loading&hellip;
          </span>

          <div class="mt-3">
            <strong>Filters</strong>
            <select class="form-control" v-model="contactCatchupFilter">
              <option :value="null">No filter</option>
              <option value="no_scheduled_catchup_catchup_overdue"
                >Catch-up overdue</option
              >
              <option value="no_scheduled_catchup_catchup_due_soon">
                Catch-up due soon
              </option>
              <option value="no_catchup_schedule_set">
                Catch-up reminders disabled
              </option>
              <option value="catchup_schedule_set">
                Catch-up reminders enabled
              </option>
            </select>
          </div>
        </div>

        <div slot="footer">
          <n-button
            type="primary"
            round
            block
            @click.native="showAddContactModal = true"
          >
            <i class="fa fa-user-plus"></i>&nbsp; Add new contact
          </n-button>
        </div>
      </card>

      <modal
        :show.sync="showAddContactModal"
        headerClasses="justify-content-center"
      >
        <h4 slot="header" class="title title-up">Add contact</h4>

        <div>
          <div class="form-group form-row">
            <div class="col-sm-4">
              <label>First name</label>
            </div>

            <fg-input
              v-model="newContact.firstName"
              type="text"
              name="firstName"
              class="form-control-md col-sm-8"
              placeholder="First name"
              addon-left-icon="now-ui-icons users_circle-08"
            ></fg-input>
          </div>

          <div class="form-group form-row">
            <div class="col-sm-4">
              <label>Last name</label>
            </div>

            <fg-input
              v-model="newContact.lastName"
              type="text"
              name="lastName"
              class="form-control-md col-sm-8"
              placeholder="Last name"
              addon-left-icon="now-ui-icons text_caps-small"
            ></fg-input>
          </div>

          <div class="form-group row">
            <label for="catchupEnabled" class="col-sm-8 col-form-label">
              Get catch-up reminders for
              <span v-if="newContact.firstName">{{
                newContact.firstName
              }}</span>
              <span v-else>this person</span>
            </label>
            <div class="col-sm-4 text-right">
              <n-switch
                v-model="newContact.catchupEnabled"
                name="catchupEnabled"
                type="primary"
                on-text="ON"
                off-text="OFF"
                class="mt-2"
              ></n-switch>
            </div>
          </div>

          <div v-if="newContact.catchupEnabled">
            <div class="form-group row">
              <label class="col-sm-4 col-form-label">Every</label>

              <div class="col-sm-3">
                <select
                  class="form-control"
                  v-model="newContact.catchupSchedule.frequency"
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                  <option value="11">11</option>
                  <option value="12">12</option>
                </select>
              </div>

              <div class="col-sm-5">
                <select
                  class="form-control"
                  v-model="newContact.catchupSchedule.interval"
                >
                  <option value="day">
                    <span v-if="newContact.catchupSchedule.frequency > 1"
                      >days</span
                    >
                    <span v-else>day</span>
                  </option>
                  <option value="week">
                    <span v-if="newContact.catchupSchedule.frequency > 1"
                      >weeks</span
                    >
                    <span v-else>week</span>
                  </option>
                  <option value="month">
                    <span v-if="newContact.catchupSchedule.frequency > 1"
                      >months</span
                    >
                    <span v-else>month</span>
                  </option>
                  <option value="year">
                    <span v-if="newContact.catchupSchedule.frequency > 1"
                      >years</span
                    >
                    <span v-else>year</span>
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group row">
              <label class="col-sm-4 col-form-label">Last catch-up</label>

              <fg-input class="col-sm-5">
                <el-date-picker
                  :disabled="newContact.noPreviousCatchup"
                  type="date"
                  placeholder="Date"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  v-model="newContact.previousCatchupDate"
                ></el-date-picker>
              </fg-input>

              <div class="col-sm-3">
                <checkbox v-model="newContact.noPreviousCatchup"
                  >Never</checkbox
                >
              </div>
            </div>
          </div>

          <div class="alert alert-warning" v-if="newContactError">
            {{ newContactError }}
          </div>
        </div>
        <template slot="footer">
          <n-button @click.native="showAddContactModal = false">
            <i class="fa fa-times"></i>&nbsp; Cancel
          </n-button>
          <n-button
            type="success"
            @click.native="createContact"
            :disabled="saving"
          >
            <span v-if="!saving"> <i class="fa fa-check"></i>&nbsp; Save </span>
            <span v-else>
              <i class="fa fa-circle-o-notch fa-spin"></i>&nbsp; Please
              wait&hellip;
            </span>
          </n-button>
        </template>
      </modal>
    </div>

    <div class="col-md-10 order-sm-1">
      <div
        class="row"
        v-match-heights="{
          el: [
            '.card-contact .author',
            '.card-contact .description',
            '.card-contact .card-body'
          ], // Array of selectors to fix
          disabled: [767]
        }"
      >
        <div
          v-if="!contacts.length"
          class="col-lg-4 offset-lg-4 col-md-4 offset-md-4"
        >
          <card v-if="!contactCatchupFilter">
            <div slot="header">
              <h5 class="card-title text-center">
                Welcome<span v-if="user.firstName">, {{ user.firstName }}</span>
              </h5>
            </div>

            <div class="text-center">
              <i class="fa fa-smile-o text-muted" style="font-size: 7em"></i>
              <p class="lead mt-3">
                Ready to set up your contacts?
              </p>

              <button
                v-on:click="showAddContactModal = true"
                class="btn btn-primary btn-lg"
              >
                <i class="fa fa-user-plus"></i>&nbsp; Add new contact
              </button>
            </div>
          </card>

          <card v-else>
            <div slot="header">
              <h5 class="card-title text-center">
                No matching contacts
              </h5>
            </div>

            <div class="text-center">
              <i
                class="fa fa-snapchat-ghost text-muted"
                style="font-size: 7em"
              ></i>
              <p class="lead mt-3">
                We didn't find anyone matching your criteria.
              </p>

              <button
                v-on:click="contactCatchupFilter = null"
                class="btn btn-default btn-lg"
              >
                <i class="fa fa-sliders"></i>&nbsp; Reset filters
              </button>
            </div>
          </card>
        </div>
        <div
          v-for="contact in contacts"
          v-bind:key="contact.id"
          class="col-lg-3 col-md-4"
        >
          <contact-card
            :contact="contact"
            :link="`contacts/${contact.id}`"
            v-on:contact-updated="refreshContact"
            v-on:contact-deleted="handleContactDeletion"
          ></contact-card>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import moment from "moment";

import { Modal } from "src/components";
import { Checkbox, Switch } from "src/components/index";
import { DatePicker } from "element-ui";
import ContactCard from "src/components/Cards/ContactCard.vue";

export default {
  components: {
    ContactCard,
    Checkbox,
    Modal,
    [Switch.name]: Switch,
    [DatePicker.name]: DatePicker
  },
  data() {
    return {
      contacts: [],
      totalContacts: 0,
      loading: false,
      showAddContactModal: false,
      saving: false,
      newContact: {
        firstName: null,
        lastName: null,
        catchupEnabled: false,
        catchupSchedule: {
          frequency: 3,
          interval: "month"
        },
        previousCatchupDate: null,
        noPreviousCatchup: false
      },
      newContactError: null,
      contactCatchupFilter: null
    };
  },
  computed: {
    user: function() {
      return this.$auth.user();
    }
  },
  watch: {
    contactCatchupFilter: function(val, oldVal) {
      this.loadContacts();
    }
  },
  methods: {
    loadContacts: function() {
      // load from API, including any set filters
      this.loading = true;
      const query = { sort: "lastName" };

      if (
        this.contactCatchupFilter === "no_scheduled_catchup_catchup_overdue"
      ) {
        query.nextCatchup = "null";
        query.catchupDueOn = {
          "<=": moment().format("YYYY-MM-DD")
        };
      } else if (
        this.contactCatchupFilter === "no_scheduled_catchup_catchup_due_soon"
      ) {
        query.nextCatchup = "null";
        query.catchupDueOn = {
          "<=": moment()
            .add(2, "weeks")
            .format("YYYY-MM-DD")
        };
      } else if (this.contactCatchupFilter === "no_catchup_schedule_set") {
        query.catchupSchedule = "null";
      } else if (this.contactCatchupFilter === "catchup_schedule_set") {
        query.catchupSchedule = "!null";
      }

      this.axios
        .get("/v1/contacts", { params: query })
        .then(response => {
          this.loading = false;
          this.contacts = response.data;
          if (response.headers["total-count"]) {
            this.totalContacts = response.headers["total-count"];
          }

          // trigger matchheights plugin
          this.$nextTick(() => {
            document.dispatchEvent(new CustomEvent("matchheight", {}));
          });
        })
        .catch(error => {
          this.loading = false;
          // TODO: handle error in UI
        });
    },
    refreshContact: function(contact) {
      this.contacts.forEach((item, index) => {
        if (item.id === contact.id) {
          this.contacts.splice(index, 1, contact);
        }
      });

      // trigger matchheights plugin
      this.$nextTick(() => {
        document.dispatchEvent(new CustomEvent("matchheight", {}));
      });
    },
    handleContactDeletion: function(contact) {
      this.contacts.forEach((item, index) => {
        if (item.id === contact.id) {
          this.contacts.splice(index, 1);
        }
      });

      // reduce total count
      this.totalContacts = this.totalContacts - 1;

      // trigger matchheights plugin
      this.$nextTick(() => {
        document.dispatchEvent(new CustomEvent("matchheight", {}));
      });
    },
    createContact: function() {
      const instance = this;
      // create contact, and IF last catchup set, create catchup too
      this.saving = true;

      const requestBody = {};

      if (instance.newContact.firstName) {
        requestBody.firstName = instance.newContact.firstName;
      }

      if (instance.newContact.lastName) {
        requestBody.lastName = instance.newContact.lastName;
      }

      if (instance.newContact.catchupEnabled === true) {
        requestBody.catchupSchedule = {
          frequency: instance.newContact.catchupSchedule.frequency,
          interval: instance.newContact.catchupSchedule.interval
        };
      }

      instance.axios
        .post("/v1/contacts", requestBody)
        .then(response => {
          const contact = response.data;
          if (
            instance.newContact.noPreviousCatchup === true ||
            !instance.newContact.previousCatchupDate
          ) {
            return true;
          } else {
            // create a catchup
            const catchupRequestBody = {
              contactId: contact.id,
              date: instance.newContact.previousCatchupDate
            };
            return instance.axios.post("/v1/catchups", catchupRequestBody);
          }
        })
        .then(() => {
          instance.saving = false;

          // reset add contact form to defaults
          instance.newContact = {
            firstName: null,
            lastName: null,
            catchupEnabled: false,
            catchupSchedule: {
              frequency: 3,
              interval: "month"
            },
            previousCatchupDate: null,
            noPreviousCatchup: false
          };
          // close the modal
          instance.showAddContactModal = false;

          // TODO: show toast notif

          // reload the contacts list
          instance.loadContacts();
        })
        .catch(error => {
          this.saving = false;
          // TODO: handle error in UI
        });
    }
  },
  mounted() {
    this.loadContacts();
  }
};
</script>
<style></style>
